import React, { useState, useEffect, useRef } from 'react';
import { Plus, UserPlus, X, UserCheck } from 'lucide-react';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { CreateGuestDonorData, GuestDonorsAPI } from '../../lib/sdk/index';
import { toast } from 'react-toastify';
import { useOffices, useCreateGuestDonor, useConvertGuestDonorToPermanent } from '../../hooks/useQueries';
import { printDonorRegistrationSlip } from '../../lib/printDonorSlip';
import {
  formatDonorApiError,
  normalizeOptionalEmail,
  normalizeOptionalPhone,
  normalizeOptionalText,
  summarizeFieldErrors,
} from '../../lib/donorFormUtils';

const AddGuestDonors: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showConvertForm, setShowConvertForm] = useState(false);
  const [convertFormData, setConvertFormData] = useState({
    guestId: '',
    cnic: ''
  });
  const [convertErrors, setConvertErrors] = useState<{[key: string]: string}>({});
  // Use React Query hooks
  const { data: officesResponse = [] } = useOffices();
  const offices = Array.isArray(officesResponse?.data) ? officesResponse.data : (Array.isArray(officesResponse) ? officesResponse : []);
  
  const [guestDonorDetails, setGuestDonorDetails] = useState<any>(null);
  // Two independent dropdowns share local state names but differ by ref keys
  const [showCreateOfficeDropdown, setShowCreateOfficeDropdown] = useState(false);
  const [createOfficeSearchTerm, setCreateOfficeSearchTerm] = useState('');
  const [fetchingGuestDonor, setFetchingGuestDonor] = useState(false);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Create guest donor mutation
  const createGuestDonorMutation = useCreateGuestDonor({
    onSuccess: async (data: any) => {
      setFormData({
        name: '',
        fatherOrHusbandName: '',
        relation: 'Father',
        phone: '',
        email: '',
        address: '',
        officeId: ''
      });
      setCreateOfficeSearchTerm('');
      setErrors({});
      setLoading(false);
      // Print a registration slip with the assigned Guest Donor ID (desktop only).
      await printDonorRegistrationSlip(data, 'guest');
    },
    onError: (error: unknown) => {
      console.error('Error creating guest donor:', error);
      toast.error(formatDonorApiError(error, 'Failed to create guest donor. Please try again.'));
      setLoading(false);
    }
  });

  // Convert guest donor to permanent mutation
  const convertGuestDonorMutation = useConvertGuestDonorToPermanent({
    onSuccess: (data: any) => {
      const migrated = data?.data?.migratedCount ?? data?.migratedCount;
      const migratedMsg =
        typeof migrated === 'number' && migrated > 0
          ? ` ${migrated} donation(s) migrated.`
          : '';
      toast.success(`Guest donor converted to permanent donor successfully!${migratedMsg}`);
      // Reset convert form
      setConvertFormData({ guestId: '', cnic: '' });
      setConvertErrors({});
      setGuestDonorDetails(null);
      setShowConvertForm(false); // Hide converter after successful conversion
      setLoading(false);
    },
    onError: (error: any) => {
      console.error('Error converting guest donor:', error);
      
      // Handle different types of conversion errors
      let errorMessage = 'Failed to convert guest donor';
      
      if (error.message) {
        errorMessage = error.message; 
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid data provided. Please check your input.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Guest donor not found. Please verify the Guest Donor ID.';
      } else if (error.response?.status === 409) {
        errorMessage = 'A donor with this CNIC already exists.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      toast.error(errorMessage);
      setLoading(false);
    }
  });
  
  const [formData, setFormData] = useState<CreateGuestDonorData>({
    name: '',
    fatherOrHusbandName: '',
    relation: 'Father',
    phone: '',
    email: '',
    address: '',
    officeId: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Auto-format phone input
  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 0) return '';
    
    // If user starts typing without +92, automatically add it
    if (!value.startsWith('+92')) {
      // Always add +92- prefix for new phone numbers
      if (cleaned.length <= 10) {
        return `+92-${cleaned}`;
      }
      return `+92-${cleaned.slice(0, 10)}`;
    }
    
    // If already has +92, just format the rest
    const withoutPrefix = cleaned.slice(2); // Remove 92
    if (withoutPrefix.length <= 0) return '+92-';
    if (withoutPrefix.length <= 10) return `+92-${withoutPrefix}`;
    return `+92-${withoutPrefix.slice(0, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, phone: formatted });
    if (errors.phone) setErrors({ ...errors, phone: '' });
  };

  const validateForm = (): { valid: boolean; fieldErrors: Record<string, string> } => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Guest donor name is required';
    }

    if (!formData.fatherOrHusbandName.trim()) {
      newErrors.fatherOrHusbandName = 'Father/Husband name is required';
    }

    const phone = normalizeOptionalPhone(formData.phone);
    if (!phone) {
      newErrors.phone = 'Phone number is required for guest donors (+92-XXXXXXXXXX)';
    } else if (!/^\+92-\d{10}$/.test(phone)) {
      newErrors.phone = 'Phone must be in format +92-XXXXXXXXXX (10 digits after +92-)';
    }

    const email = normalizeOptionalEmail(formData.email);
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address, or leave email blank';
    }

    if (!formData.officeId || !formData.officeId.trim()) {
      newErrors.officeId = 'Office is required — search and select an office from the list';
    }

    setErrors(newErrors);
    return { valid: Object.keys(newErrors).length === 0, fieldErrors: newErrors };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Guard against rapid double-submits (double-click / slow network).
    if (createGuestDonorMutation.isLoading) {
      return;
    }

    const { valid, fieldErrors } = validateForm();
    if (!valid) {
      toast.error(summarizeFieldErrors(fieldErrors));
      return;
    }

    createGuestDonorMutation.mutate({
      ...formData,
      phone: normalizeOptionalPhone(formData.phone),
      email: normalizeOptionalEmail(formData.email),
      address: normalizeOptionalText(formData.address),
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      fatherOrHusbandName: '',
      relation: 'Father',
      phone: '',
      email: '',
      address: '',
      officeId: ''
    });
    setCreateOfficeSearchTerm('');
    setErrors({});
  };

  // Load offices on component mount
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      setIsDebouncing(false);
    };
  }, []);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (_event: MouseEvent) => {
      if (showCreateOfficeDropdown) setShowCreateOfficeDropdown(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCreateOfficeDropdown]);


  // Fetch guest donor details by ID
  const fetchGuestDonorDetails = async (guestId: string) => {
    if (!guestId.trim()) {
      setGuestDonorDetails(null);
      setConvertErrors({});
      return;
    }

    try {
      setFetchingGuestDonor(true);
      const donor = await GuestDonorsAPI.getGuestDonorByGuestId(guestId.trim());
      setGuestDonorDetails(donor);
      setConvertErrors({});

    } catch (error: any) {
      console.error('Error fetching guest donor:', error);
      setGuestDonorDetails(null);
      
      // Handle different types of errors with specific messages
      if (error.response?.status === 404) {
        // For 404 errors, don't show an error message - just show "No guest donor found"
        // This allows the user to continue typing without the field being marked as invalid
        setConvertErrors({});
      } else if (error.response?.status === 400) {
        setConvertErrors({ guestId: 'Invalid guest donor ID format' });
      } else if (error.response?.status === 500) {
        setConvertErrors({ guestId: 'Server error. Please try again later.' });
      } else if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        setConvertErrors({ guestId: 'Network error. Please check your connection.' });
      } else if (error.message === 'Guest donor not found') {
        // For "Guest donor not found" errors, don't show validation error
        setConvertErrors({});
      } else {
        // For other errors, show a generic message
        setConvertErrors({ guestId: 'Failed to fetch guest donor details. Please try again.' });
      }
    } finally {
      setFetchingGuestDonor(false);
    }
  };

  // Format CNIC input
  const formatCNIC = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 0) return '';
    
    if (cleaned.length <= 5) {
      return cleaned;
    } else if (cleaned.length <= 12) {
      return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
    } else {
      return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 12)}-${cleaned.slice(12, 13)}`;
    }
  };

  const formatGuestOfficeLabel = (donor: { officeId?: string | { _id: string; name: string; officeNo: number } }) => {
    const office = donor.officeId;
    if (!office) return 'Not assigned';
    if (typeof office === 'object') {
      return `${office.name} (Office #${office.officeNo})`;
    }
    const matched = offices.find(o => o._id === office);
    return matched ? `${matched.name} (Office #${matched.officeNo})` : 'Assigned';
  };

  // Filter offices for the create-guest-donor form
  const filteredCreateOffices = offices.filter(office =>
    office.name.toLowerCase().includes(createOfficeSearchTerm.toLowerCase()) ||
    office.officeNo.toString().includes(createOfficeSearchTerm)
  );

  const handleConvertGuestDonor = async () => {
    const newConvertErrors: {[key: string]: string} = {};

    if (!convertFormData.guestId.trim()) {
      newConvertErrors.guestId = 'Guest Donor ID is required';
    }
    if (!convertFormData.cnic.trim()) {
      newConvertErrors.cnic = 'CNIC is required';
    }
    if (!guestDonorDetails?.officeId) {
      newConvertErrors.guestId = 'Guest donor must have an office assigned before conversion';
    }

    setConvertErrors(newConvertErrors);

    if (Object.keys(newConvertErrors).length > 0) {
      return;
    }

    setLoading(true);
    convertGuestDonorMutation.mutate(convertFormData);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Add New Guest Donor</h1>
        <p className="text-gray-600 dark:text-gray-400">Create a new guest donor (CNIC not required)</p>
      </div>

      {/* Information Card */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start">
          <UserPlus className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Guest Donor Information</h3>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Guest donors do not require a CNIC, but they must be assigned to an office (just like regular donors).
              All fields marked with * are required. The guest donor will receive an auto-incremented guest ID (e.g., G001, G002) upon creation.
              Guest donors can later be converted to permanent donors by providing their CNIC. The assigned office is preserved automatically.
            </p>
          </div>
        </div>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">Guest Donor Name *</label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (errors.name) setErrors({ ...errors, name: '' });
                }}
                placeholder="Enter guest donor's full name"
                className={errors.name ? 'border-red-500' : ''}
                required
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="form-label">Father/Husband Name *</label>
              <Input
                type="text"
                value={formData.fatherOrHusbandName}
                onChange={(e) => {
                  setFormData({ ...formData, fatherOrHusbandName: e.target.value });
                  if (errors.fatherOrHusbandName) setErrors({ ...errors, fatherOrHusbandName: '' });
                }}
                placeholder="Enter father or husband name"
                className={errors.fatherOrHusbandName ? 'border-red-500' : ''}
                required
              />
              {errors.fatherOrHusbandName && <p className="text-red-500 text-xs mt-1">{errors.fatherOrHusbandName}</p>}
            </div>

            <div>
              <label className="form-label">Relation *</label>
              <select
                value={formData.relation}
                onChange={(e) => setFormData({ ...formData, relation: e.target.value as 'Father' | 'Husband' })}
                className="form-select"
                required
              >
                <option value="Father">Father</option>
                <option value="Husband">Husband</option>
              </select>
            </div>

            <div>
              <label className="form-label">Phone Number *</label>
              <Input
                type="text"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="Add number without 0, e.g. 3331112223"
                maxLength={14}
                className={errors.phone ? 'border-red-500' : ''}
                required
              />
              <p className="text-xs text-gray-500 mt-1">Automatically formats to +92-XXXXXXXXXX format</p>
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label className="form-label">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  if (errors.email) setErrors({ ...errors, email: '' });
                }}
                placeholder="Enter email address"
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="form-label">Address</label>
              <Input
                type="text"
                value={formData.address || ''}
                onChange={(e) => {
                  setFormData({ ...formData, address: e.target.value });
                }}
                placeholder="Enter address (optional)"
                className=""
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="form-label">Office *</label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search offices..."
                  value={createOfficeSearchTerm}
                  onChange={(e) => {
                    setCreateOfficeSearchTerm(e.target.value);
                    setShowCreateOfficeDropdown(true);
                  }}
                  onFocus={() => setShowCreateOfficeDropdown(true)}
                  onMouseDown={(e) => e.stopPropagation()}
                  className={errors.officeId ? 'border-red-500' : ''}
                  required
                />

                {showCreateOfficeDropdown && (
                  <div
                    className="absolute z-10 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {filteredCreateOffices.map((office) => (
                      <div
                        key={office._id}
                        className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFormData({ ...formData, officeId: office._id });
                          setCreateOfficeSearchTerm(`${office.name} (Office #${office.officeNo})`);
                          setShowCreateOfficeDropdown(false);
                          if (errors.officeId) setErrors({ ...errors, officeId: '' });
                        }}
                      >
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {office.name} - Office #{office.officeNo}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {office.isCentral ? 'Central Office' : 'Regional Office'}
                        </div>
                      </div>
                    ))}
                    {filteredCreateOffices.length === 0 && (
                      <div className="px-3 py-2 text-gray-500 dark:text-gray-400">
                        {createOfficeSearchTerm ? 'No offices found' : 'Type to search offices...'}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {formData.officeId && (
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Selected Office: <span className="font-medium text-gray-900 dark:text-gray-100">
                    {offices.find(o => o._id === formData.officeId)?.name || 'Unknown'}
                  </span>
                </div>
              )}

              {errors.officeId && <p className="text-red-500 text-xs mt-1">{errors.officeId}</p>}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex space-x-3 pt-4">
            <Button type="submit" className="flex items-center gap-2" disabled={createGuestDonorMutation.isLoading}>
              {createGuestDonorMutation.isLoading ? <LoadingSpinner size="sm" /> : <Plus className="h-4 w-4" />}
              {createGuestDonorMutation.isLoading ? 'Creating...' : 'Create Guest Donor'}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm} className="flex items-center gap-2">
              <X className="h-4 w-4" />
              Reset Form
            </Button>
          </div>
        </form>
      </div>

      {/* Convert Guest Donor Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Convert Guest Donor to Permanent Donor</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Convert an existing guest donor to a permanent donor by providing CNIC (office is taken from the guest record)</p>
          </div>
          <Button 
            onClick={() => setShowConvertForm(!showConvertForm)} 
            variant="outline"
            className="flex items-center gap-2"
          >
            <UserCheck className="h-4 w-4" />
            {showConvertForm ? 'Hide Converter' : 'Show Converter'}
          </Button>
        </div>

        {showConvertForm && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Guest Donor ID *</label>
                <div className="relative">
                  <Input
                    type="text"
                    value={convertFormData.guestId}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      setConvertFormData({ ...convertFormData, guestId: value });
                      
                      // Clear previous results and errors when user starts typing
                      if (guestDonorDetails) {
                        setGuestDonorDetails(null);
                      }
                      if (convertErrors.guestId) {
                        setConvertErrors({ ...convertErrors, guestId: '' });
                      }
                      
                      // Clear any existing timeout
                      if (debounceTimeoutRef.current) {
                        clearTimeout(debounceTimeoutRef.current);
                      }
                      
                      // Set debouncing state to true
                      setIsDebouncing(true);
                      
                      // Set a new timeout for debounced search
                      debounceTimeoutRef.current = setTimeout(() => {
                        // Only fetch donor details when we have a complete guest donor ID format
                        // Format should be G followed by at least 3 digits (e.g., G001, G002, G010, G100)
                        const guestIdPattern = /^G\d{3,}$/;
                        if (guestIdPattern.test(value)) {
                          fetchGuestDonorDetails(value);
                        } else {
                          // Clear donor details if ID is incomplete
                          setGuestDonorDetails(null);
                        }
                        // Clear debouncing state
                        setIsDebouncing(false);
                      }, 500); // 500ms delay
                    }}
                    placeholder="e.g., G001"
                    className={`${convertErrors.guestId ? 'border-red-500' : ''} ${fetchingGuestDonor ? 'pr-10' : ''} ${isDebouncing ? 'border-blue-300' : ''}`}
                  />
                  {isDebouncing && (
                    <div className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                      Searching...
                    </div>
                  )}
                  {fetchingGuestDonor && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <LoadingSpinner size="sm" />
                    </div>
                  )}
                </div>
                
                {/* Guest Donor Details Display */}
                {convertFormData.guestId && guestDonorDetails && !fetchingGuestDonor && (
                  <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">Guest Donor Found</p>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      <strong>Name:</strong> {guestDonorDetails.name}
                    </p>
                    <p className="text-sm text-green-600 dark:text-green-400">
                      <strong>Phone:</strong> {guestDonorDetails.phone}
                    </p>
                    <p className="text-sm text-green-600 dark:text-green-400">
                      <strong>Office:</strong> {formatGuestOfficeLabel(guestDonorDetails)}
                    </p>
                  </div>
                )}
                
                {/* Error Display */}
                {convertErrors.guestId && !fetchingGuestDonor && (
                  <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <p className="text-sm font-medium text-red-800 dark:text-red-200">Error</p>
                    </div>
                    <p className="text-sm text-red-700 dark:text-red-300">{convertErrors.guestId}</p>
                  </div>
                )}
                
                {/* Help Text */}
                {convertFormData.guestId && !guestDonorDetails && !convertErrors.guestId && !fetchingGuestDonor && (
                  <p className="text-xs text-gray-500 mt-1">Enter a valid guest donor ID (e.g., G001, G002)</p>
                )}
                
                {/* No Guest Donor Found Message */}
                {convertFormData.guestId && !guestDonorDetails && !convertErrors.guestId && !fetchingGuestDonor && 
                 /^G\d{3,}$/.test(convertFormData.guestId) && (
                  <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">No Guest Donor Found</p>
                    </div>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      No guest donor found with ID "{convertFormData.guestId}". Please verify the ID or create a new guest donor first.
                    </p>
                  </div>
                )}
                
                
              </div>

              <div>
                <label className="form-label">CNIC *</label>
                <Input
                  type="text"
                  value={convertFormData.cnic}
                  onChange={(e) => {
                    const formatted = formatCNIC(e.target.value);
                    setConvertFormData({ ...convertFormData, cnic: formatted });
                    if (convertErrors.cnic) setConvertErrors({ ...convertErrors, cnic: '' });
                  }}
                  placeholder="XXXXX-XXXXXXX-X"
                  maxLength={15}
                  className={convertErrors.cnic ? 'border-red-500' : ''}
                />
                {convertErrors.cnic && <p className="text-red-500 text-xs mt-1">{convertErrors.cnic}</p>}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Office is taken from the guest record (shown above after lookup).
                </p>
              </div>

            </div>

            <div className="flex space-x-3">
              <Button 
                onClick={handleConvertGuestDonor} 
                className="flex items-center gap-2"
                disabled={loading || !guestDonorDetails}
              >
                {loading ? <LoadingSpinner size="sm" /> : <UserCheck className="h-4 w-4" />}
                {loading ? 'Converting...' : 'Convert to Permanent Donor'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowConvertForm(false);
                  setConvertFormData({ guestId: '', cnic: '' });
                  setConvertErrors({});
                  setGuestDonorDetails(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default AddGuestDonors;
